import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Descriptions, Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import BinContext from "../../../../context/wms/bin/context";
import BinContextProvider from "../../../../context/wms/bin/provider";
import { BinPosition } from "../../../../models/wms/enums";
import Spin from "../../../general/antd/spin";
import PageDetails from "../../../general/details";

const Details = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { details, actions, loading } = useContext(BinContext);

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  if (loading.includes("details")) {
    return <Spin />;
  }

  const getBinPositionLabel = (pos?: BinPosition) => {
    if (pos === BinPosition.LEFT) return t("position_left");
    if (pos === BinPosition.CENTER) return t("position_center");
    if (pos === BinPosition.RIGHT) return t("position_right");
    return "-";
  };

  return (
    <PageDetails
      title={t("details")}
      subtitle={t("bin")}
      buttonName={`${t("update_bin")}`}
      onClick={() => window.location.assign(`/wms/bins/update/${id}`)}
      loading={loading.includes("details")}
      onRefresh={() => actions.getDetails(Number(id))}
    >
      <Descriptions
        bordered
        column={1}
        size="middle"
      >
        <Descriptions.Item label={t("id")}>{details?.id}</Descriptions.Item>
        {details?.translations?.name?.map((translation) => (
          <Descriptions.Item
            key={translation.lang}
            label={`${t("name")} (${translation.lang})`}
          >
            {translation.value || "-"}
          </Descriptions.Item>
        ))}
        <Descriptions.Item label={t("store")}>
          {details?.store?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("store_id")}>
          {details?.storeId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("zone")}>
          {details?.zone?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("zone_id")}>
          {details?.zoneId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("aisle")}>
          {details?.aisle?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("aisle_id")}>
          {details?.aisleId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("storage_unit")}>
          {details?.storageUnit?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("storage_unit_id")}>
          {details?.storageUnitId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("shelf")}>
          {details?.shelf?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("shelf_id")}>
          {details?.shelfId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("scanable_code")}>
          {details?.scanableCode || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("bin_position")}>
          {getBinPositionLabel(details?.binPosition)}
        </Descriptions.Item>
        <Descriptions.Item label={t("pick_sequence")}>
          {details?.pickSequence}
        </Descriptions.Item>
        <Descriptions.Item label={t("is_active")}>
          {details?.isActive ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              {t("active")}
            </Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="default">
              {t("inactive")}
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("created_at")}>
          {details?.createdAt ? String(details.createdAt) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("created_by")}>
          {details?.createdBy
            ? `${details.createdBy.firstName} ${details.createdBy.lastName} (${details.createdBy.mobile})`
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("updated_at")}>
          {details?.updatedAt ? String(details.updatedAt) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("updated_by")}>
          {details?.updatedBy
            ? `${details.updatedBy.firstName} ${details.updatedBy.lastName} (${details.updatedBy.mobile})`
            : "-"}
        </Descriptions.Item>
      </Descriptions>
    </PageDetails>
  );
};

const BinDetails = () => {
  return (
    <BinContextProvider>
      <Details />
    </BinContextProvider>
  );
};

export default BinDetails;
